<template>
    <div class="ticket-view-page">
        <ComponentHeader
            title="View Support Ticket"
            :hasSearch="false"
            :hasCreate="false"
            :hasReload="false"
            :hasback="true"
            @back-press="goBack"
        />
        <div
            v-if="loading"
            class="d-flex flex-column align-items-center justify-content-center py-5"
        >
            <atom-spinner
                :animation-duration="1000"
                :size="60"
                color="#ff1d5e"
                class="my-5"
            />
            <p class="text-muted mb-0">Loading...</p>
        </div>
        <div v-else-if="!ticketData" class="text-center py-5">
            <p class="text-muted">Ticket not found.</p>
            <b-button variant="primary" @click="goBack">
                <i class="fa fa-arrow-left mr-1"></i>Back to Tickets
            </b-button>
        </div>
        <div v-else class="ticket-view-content">
            <!-- Header Section -->
            <b-card class="card-wrap ticket-hero-card" border-variant="info">
                <div class="ticket-hero">
                    <div class="hero-left">
                        <div class="header-meta">
                            <span class="ticket-id-display">{{
                                ticketDisplayId
                            }}</span>
                            <span
                                :class="[
                                    'status-badge',
                                    'badge-status-' + normalizedStatus,
                                ]"
                            >
                                {{ displayStatus }}
                            </span>
                            <span
                                :class="[
                                    'severity-badge',
                                    'badge-severity-' + severityClass,
                                ]"
                            >
                                {{ displaySeverity }}
                            </span>
                        </div>
                        <h1 class="ticket-title">
                            {{
                                ticketData.subject ||
                                ticketData.title ||
                                "Untitled"
                            }}
                        </h1>
                    </div>
                    <div class="header-actions">
                        <b-button
                            variant="danger"
                            size="sm"
                            class="action-btn escalate-btn"
                            @click="showEscalateModal = true"
                        >
                            <i class="fa fa-exclamation-circle mr-1"></i
                            >ESCALATE
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            class="action-btn"
                            @click="syncTicket"
                        >
                            <i class="fa fa-refresh mr-1"></i>Sync
                        </b-button>
                    </div>
                </div>
            </b-card>

            <!-- Main 2-Column Layout -->
            <b-row>
                <!-- LEFT PANEL (8 cols) -->
                <b-col cols="12" lg="8" class="mb-4">
                    <!-- Issue Context Card -->
                    <b-card
                        class="card-wrap section-card issue-context-card mb-4"
                        no-body
                    >
                        <template #header>
                            <div class="card-header-custom">
                                <i class="fa fa-info-circle mr-2"></i>
                                <span>ISSUE CONTEXT</span>
                            </div>
                        </template>
                        <b-card-body>
                            <div class="context-grid">
                                <div class="context-item">
                                    <i
                                        class="fa fa-file-text-o text-muted mr-2"
                                    ></i>
                                    <span class="context-label"
                                        >Ticket Number:</span
                                    >
                                    <span class="context-value">{{
                                        ticketDisplayId
                                    }}</span>
                                </div>
                                <div class="context-item">
                                    <i class="fa fa-user text-muted mr-2"></i>
                                    <span class="context-label">CRM Name:</span>
                                    <span class="context-value">{{
                                        crmName
                                    }}</span>
                                </div>
                                <div class="context-item">
                                    <i
                                        class="fa fa-th-large text-muted mr-2"
                                    ></i>
                                    <span class="context-label">Category:</span>
                                    <span class="context-value">{{
                                        categoryName
                                    }}</span>
                                </div>
                                <div class="context-item">
                                    <i
                                        class="fa fa-calendar text-muted mr-2"
                                    ></i>
                                    <span class="context-label"
                                        >Created Date:</span
                                    >
                                    <span class="context-value">{{
                                        formattedCreatedDate
                                    }}</span>
                                </div>
                                <div v-if="displayScope" class="context-item">
                                    <i class="fa fa-globe text-muted mr-2"></i>
                                    <span class="context-label">Scope:</span>
                                    <span class="context-value">{{
                                        displayScope
                                    }}</span>
                                </div>
                                <div v-if="displayTenant" class="context-item">
                                    <i
                                        class="fa fa-building text-muted mr-2"
                                    ></i>
                                    <span class="context-label">Tenant:</span>
                                    <span class="context-value">{{
                                        displayTenant
                                    }}</span>
                                </div>
                            </div>
                            <!-- Description (inside same card) -->
                            <div
                                class="description-section mt-4 pt-3 border-top"
                            >
                                <h6
                                    class="text-uppercase text-muted mb-2 font-weight-bold"
                                >
                                    Description
                                </h6>
                                <div class="description-text">
                                    {{
                                        ticketData.description ||
                                        "No description provided."
                                    }}
                                </div>
                            </div>
                        </b-card-body>
                    </b-card>

                    <!-- Interaction Timeline Card -->
                    <b-card
                        class="card-wrap section-card timeline-card"
                        no-body
                    >
                        <template #header>
                            <div class="card-header-custom">
                                <i class="fa fa-clock-o mr-2"></i>
                                <span>INTERACTION TIMELINE</span>
                            </div>
                        </template>
                        <b-card-body>
                            <b-tabs
                                v-model="timelineTabIndex"
                                class="timeline-tabs"
                            >
                                <b-tab title="ACTIVITY" active>
                                    <div class="timeline-placeholder">
                                        <div
                                            v-for="(
                                                entry, idx
                                            ) in activityTimeline"
                                            :key="entry._id || idx"
                                            class="timeline-item"
                                        >
                                            <div class="timeline-icon">
                                                <i
                                                    class="fa fa-arrow-up text-warning"
                                                ></i>
                                            </div>
                                            <div class="timeline-content">
                                                <div class="timeline-actor">
                                                    {{
                                                        (entry.updatedBy &&
                                                            entry.updatedBy
                                                                .name) ||
                                                        "System"
                                                    }}
                                                </div>
                                                <div class="timeline-text">
                                                    {{
                                                        entry.action ||
                                                        entry.description ||
                                                        "Ticket created"
                                                    }}
                                                </div>
                                                <div
                                                    v-if="
                                                        entry.description &&
                                                        entry.action
                                                    "
                                                    class="timeline-description"
                                                >
                                                    {{ entry.description }}
                                                </div>
                                                <div class="timeline-date">
                                                    {{
                                                        formatTimelineDate(
                                                            entry.updatedAt,
                                                        )
                                                    }}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            v-if="!activityTimeline.length"
                                            class="timeline-item"
                                        >
                                            <div class="timeline-icon">
                                                <i
                                                    class="fa fa-arrow-up text-warning"
                                                ></i>
                                            </div>
                                            <div class="timeline-content">
                                                <div class="timeline-actor">
                                                    System
                                                </div>
                                                <div class="timeline-text">
                                                    Ticket created
                                                </div>
                                                <div class="timeline-date">
                                                    {{
                                                        formattedCreatedDateTime
                                                    }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </b-tab>
                                <b-tab
                                    :title="'COMMENTS (' + commentCount + ')'"
                                >
                                    <div class="timeline-placeholder">
                                        <div
                                            v-for="(
                                                entry, idx
                                            ) in commentTimeline"
                                            :key="entry._id || idx"
                                            class="timeline-item timeline-comment-item"
                                        >
                                            <div class="timeline-icon">
                                                <i
                                                    class="fa fa-comment text-info"
                                                ></i>
                                            </div>
                                            <div class="timeline-content">
                                                <div class="timeline-actor">
                                                    {{
                                                        (entry.updatedBy &&
                                                            entry.updatedBy
                                                                .name) ||
                                                        "System"
                                                    }}
                                                </div>
                                                <div class="timeline-text">
                                                    {{
                                                        entry.description || "—"
                                                    }}
                                                </div>
                                                <div class="timeline-date">
                                                    {{
                                                        formatTimelineDate(
                                                            entry.updatedAt,
                                                        )
                                                    }}
                                                </div>
                                            </div>
                                            <div
                                                v-if="
                                                    getCommentImageUrls(entry)
                                                        .length
                                                "
                                                class="comment-attachment-icons"
                                            >
                                                <button
                                                    v-for="(
                                                        imgUrl, i
                                                    ) in getCommentImageUrls(
                                                        entry,
                                                    )"
                                                    :key="i"
                                                    type="button"
                                                    class="comment-img-thumb-btn"
                                                    @click="
                                                        openImagePreview(imgUrl)
                                                    "
                                                    title="View image"
                                                >
                                                    <img
                                                        :src="imgUrl"
                                                        alt="Comment attachment"
                                                        class="comment-img-thumb"
                                                        @error="
                                                            $event.target.style.display =
                                                                'none';
                                                            $event.target
                                                                .nextElementSibling &&
                                                                ($event.target.nextElementSibling.style.display =
                                                                    'inline');
                                                        "
                                                    />
                                                    <i
                                                        class="fa fa-image comment-img-fallback"
                                                        style="display: none"
                                                    ></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            v-if="!commentTimeline.length"
                                            class="timeline-placeholder text-muted text-center py-4"
                                        >
                                            No comments yet
                                        </div>
                                    </div>
                                </b-tab>
                                <b-tab
                                    :title="
                                        'ATTACHMENTS (' + attachmentCount + ')'
                                    "
                                >
                                    <div class="attachments-list">
                                        <div
                                            v-for="(att, idx) in attachmentList"
                                            :key="att._id || idx"
                                            class="attachment-item"
                                        >
                                            <div
                                                v-if="isImageAttachment(att)"
                                                class="attachment-image-wrap"
                                            >
                                                <img
                                                    :src="getAttachmentUrl(att)"
                                                    :alt="
                                                        att.name || 'Attachment'
                                                    "
                                                    class="attachment-img"
                                                    @error="onImageLoadError"
                                                />
                                                <a
                                                    :href="
                                                        getAttachmentUrl(att)
                                                    "
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    class="attachment-link-fallback"
                                                    style="display: none"
                                                >
                                                    <i
                                                        class="fa fa-external-link mr-1"
                                                    ></i>
                                                    {{
                                                        att.name || "View file"
                                                    }}
                                                </a>
                                            </div>
                                            <a
                                                v-else
                                                :href="getAttachmentUrl(att)"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="attachment-link"
                                            >
                                                <i
                                                    class="fa fa-paperclip mr-1"
                                                ></i>
                                                {{
                                                    att.name ||
                                                    "View attachment"
                                                }}
                                            </a>
                                        </div>
                                        <div
                                            v-if="!attachmentList.length"
                                            class="text-muted text-center py-4"
                                        >
                                            No attachments
                                        </div>
                                    </div>
                                </b-tab>
                            </b-tabs>
                        </b-card-body>
                    </b-card>
                </b-col>

                <!-- RIGHT PANEL (4 cols) -->
                <b-col cols="12" lg="4" class="mb-4">
                    <!-- Response Health Card -->
                    <b-card
                        class="card-wrap section-card response-health-card mb-4"
                        no-body
                    >
                        <template #header>
                            <div class="card-header-custom">
                                <i class="fa fa-bolt mr-2"></i>
                                <span>RESPONSE HEALTH</span>
                            </div>
                        </template>
                        <b-card-body>
                            <div class="sla-progress">
                                <div class="sla-header">
                                    <span>SLA PROGRESS</span>
                                    <span class="sla-health"
                                        >{{ slaHealth }}% Health</span
                                    >
                                </div>
                                <b-progress
                                    :value="slaHealth"
                                    :max="100"
                                    class="sla-progress-bar"
                                ></b-progress>
                                <div class="sla-footer">
                                    <span class="sla-started"
                                        >STARTED {{ slaStarted }} AGO</span
                                    >
                                    <span
                                        v-if="slaRemaining"
                                        class="sla-remaining"
                                        >{{ slaRemaining }} REMAINING</span
                                    >
                                </div>
                            </div>
                        </b-card-body>
                    </b-card>

                    <!-- Update Status Card -->
                    <b-card
                        class="card-wrap section-card update-status-card mb-4"
                        no-body
                    >
                        <template #header>
                            <div class="card-header-custom">
                                <i class="fa fa-list-ol mr-2"></i>
                                <span>UPDATE STATUS</span>
                            </div>
                        </template>
                        <b-card-body>
                            <div class="status-options">
                                <div
                                    v-for="opt in statusOptions"
                                    :key="opt.value"
                                    :class="[
                                        'status-option',
                                        { active: currentStatus === opt.value },
                                    ]"
                                    @click="selectStatus(opt.value)"
                                >
                                    <span class="status-radio">
                                        <i
                                            v-if="currentStatus === opt.value"
                                            class="fa fa-check-circle text-primary"
                                        ></i>
                                        <i
                                            v-else
                                            class="fa fa-circle-o text-muted"
                                        ></i>
                                    </span>
                                    <span
                                        :class="[
                                            'status-label',
                                            {
                                                'text-primary font-weight-bold':
                                                    currentStatus === opt.value,
                                            },
                                        ]"
                                    >
                                        {{ opt.label }}
                                    </span>
                                    <i
                                        v-if="currentStatus === opt.value"
                                        class="fa fa-check text-primary ml-auto"
                                    ></i>
                                </div>
                            </div>
                        </b-card-body>
                    </b-card>

                    <!-- Reassign Member Card -->
                    <b-card
                        class="card-wrap section-card reassign-card"
                        no-body
                    >
                        <template #header>
                            <div class="card-header-custom">
                                <i class="fa fa-user-plus mr-2"></i>
                                <span>REASSIGN MEMBER</span>
                            </div>
                        </template>
                        <b-card-body>
                            <b-input-group class="mb-3">
                                <b-input-group-prepend is-text>
                                    <i class="fa fa-search"></i>
                                </b-input-group-prepend>
                                <b-form-input
                                    v-model="memberSearchQuery"
                                    placeholder="Search team members..."
                                    size="sm"
                                ></b-form-input>
                            </b-input-group>
                            <div class="member-list">
                                <div
                                    v-for="member in filteredTeamList"
                                    :key="member._id"
                                    :class="[
                                        'member-item',
                                        {
                                            selected:
                                                assignedToId === member._id,
                                        },
                                    ]"
                                    @click="selectMember(member)"
                                >
                                    <span class="member-avatar">{{
                                        getInitials(member)
                                    }}</span>
                                    <span class="member-name">{{
                                        getMemberName(member)
                                    }}</span>
                                    <i
                                        v-if="assignedToId === member._id"
                                        class="fa fa-check text-success ml-auto"
                                    ></i>
                                </div>
                                <div
                                    v-if="!filteredTeamList.length"
                                    class="text-muted text-center py-3 small"
                                >
                                    No team members found
                                </div>
                            </div>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>
        </div>

        <!-- Escalate Ticket Modal -->
        <b-modal
            v-model="showEscalateModal"
            size="md"
            centered
            title="Escalate Ticket"
            hide-footer
            modal-class="escalate-ticket-modal"
            @hide="resetEscalateForm"
        >
            <div class="escalate-modal-body">
                <div class="escalate-alert">
                    This will re-assign the ticket to a higher authority and
                    mark it as <strong>Critical</strong>.
                </div>

                <div class="escalate-field">
                    <label class="escalate-label">Select authority level</label>
                    <b-form-select
                        v-model="selectedAuthorityLevel"
                        :options="authorityLevelOptions"
                        class="escalate-select"
                    ></b-form-select>
                </div>
                <div class="escalate-field">
                    <label class="escalate-label"
                        >Reason for escalation
                        <span class="text-danger">*</span></label
                    >
                    <b-form-textarea
                        v-model="escalationReason"
                        placeholder="e.g. SLA breach risk, urgent customer request..."
                        rows="3"
                        class="escalate-select"
                    ></b-form-textarea>
                </div>
                <div class="escalate-field">
                    <label class="escalate-label">Target managers</label>
                    <div class="escalate-manager-list">
                        <div
                            v-for="manager in filteredEscalateManagers"
                            :key="manager._id"
                            class="escalate-manager-card"
                        >
                            <div class="escalate-manager-avatar">
                                {{ getInitials(manager) }}
                            </div>
                            <div class="escalate-manager-info">
                                <div class="escalate-manager-name">
                                    {{ getMemberName(manager) }}
                                </div>
                                <div class="escalate-manager-role">
                                    {{ getManagerRole(manager) }}
                                </div>
                            </div>
                            <b-button
                                size="sm"
                                variant="outline-danger"
                                class="escalate-assign-btn"
                                title="Escalate to this manager"
                                :disabled="!canEscalate"
                                @click="escalateToManager(manager)"
                            >
                                <i class="fa fa-arrow-up mr-1"></i>Escalate
                            </b-button>
                        </div>
                        <div
                            v-if="!filteredEscalateManagers.length"
                            class="text-muted text-center py-4"
                        >
                            No managers available
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- Image Preview Modal -->
        <b-modal
            v-model="showImageModal"
            size="lg"
            centered
            hide-footer
            title="Image Preview"
            body-class="p-0 text-center"
            @hide="imagePreviewUrl = null"
        >
            <img
                v-if="imagePreviewUrl"
                :src="imagePreviewUrl"
                alt="Preview"
                class="img-fluid"
                style="max-height: 70vh"
            />
        </b-modal>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";
import { mapGetters } from "vuex";
import { AtomSpinner } from "epic-spinners";

const STATUS_OPTIONS = [
    { value: "NEW", label: "NEW" },
    { value: "IN_PROGRESS", label: "IN PROGRESS" },
    { value: "RESOLVED", label: "RESOLVED" },
    { value: "CLOSED", label: "CLOSED" },
];

export default {
    name: "ViewTicketManagement",
    components: {
        ComponentHeader,
        AtomSpinner,
    },
    data() {
        return {
            loading: true,
            timelineTabIndex: 0,
            memberSearchQuery: "",
            statusUpdating: false,
            assignUpdating: false,
            statusOptions: STATUS_OPTIONS,
            showImageModal: false,
            imagePreviewUrl: null,
            showEscalateModal: false,
            selectedAuthorityLevel: null,
            escalationReason: "",
            authorityLevelOptions: [
                { value: null, text: "Select authority level" },
                { value: 1, text: "Tier 1" },
                { value: 2, text: "Tier 2 Technical Lead" },
                { value: 3, text: "Tier 3" },
            ],
        };
    },
    computed: {
        ...mapGetters("ticketManagement", [
            "GetTicketList",
            "GetAdminList",
            "GetCategoryList",
        ]),
        ticketId() {
            return (
                this.$route.params.id ||
                (this.$route.params.ticket && this.$route.params.ticket._id)
            );
        },
        ticketFromParams() {
            return this.$route.params.ticket || null;
        },
        getSelectedTicketFn() {
            return this.$store.getters["ticketManagement/getSelectedTicket"];
        },
        ticketFromStore() {
            if (!this.ticketId || !this.getSelectedTicketFn) return null;
            return this.getSelectedTicketFn(this.ticketId);
        },
        ticketData() {
            const fromStore = this.ticketFromStore;
            const fromParams =
                this.ticketFromParams &&
                (this.ticketFromParams.raw || this.ticketFromParams);
            return fromStore || fromParams || null;
        },
        ticketDisplayId() {
            if (!this.ticketData) return "";
            const no = this.ticketData.ticketNo;
            return no
                ? no.startsWith("#")
                    ? no
                    : "#" + no
                : "#" + (this.ticketData._id || "");
        },
        currentStatus() {
            const s = this.ticketData && this.ticketData.status;
            if (!s) return "NEW";
            const upper = String(s).toUpperCase();
            if (upper === "OPEN") return "NEW";
            if (
                [
                    "NEW",
                    "IN_PROGRESS",
                    "IN PROGRESS",
                    "RESOLVED",
                    "CLOSED",
                ].includes(upper)
            ) {
                return upper === "IN PROGRESS" ? "IN_PROGRESS" : upper;
            }
            return "NEW";
        },
        displayStatus() {
            return this.currentStatus.replace(/_/g, " ");
        },
        normalizedStatus() {
            return (this.currentStatus || "new")
                .toLowerCase()
                .replace(/_/g, "-");
        },
        displaySeverity() {
            const raw = this.ticketData;
            if (!raw) return "MEDIUM";
            if (raw.severity) return String(raw.severity).toUpperCase();
            const num = raw.priority;
            if (num == null) return "MEDIUM";
            const n = Number(num);
            if (n >= 8) return "CRITICAL";
            if (n >= 6) return "HIGH";
            if (n >= 4) return "MEDIUM";
            return "LOW";
        },
        severityClass() {
            return (this.displaySeverity || "medium").toLowerCase();
        },
        crmName() {
            const raw = this.ticketData;
            if (!raw) return "—";
            const rep = raw.assignTo || {};
            const name =
                rep.name ||
                [rep.first, rep.last].filter(Boolean).join(" ") ||
                "";
            return name || "—";
        },
        categoryName() {
            const raw = this.ticketData;
            if (!raw) return "—";
            const cat = raw.category;
            if (!cat) return raw.categoryName || "—";
            if (typeof cat === "string") {
                const list = this.GetCategoryList || [];
                const found = list.find((c) => c._id === cat);
                return found ? found.name || found.description || "—" : "—";
            }
            return cat.name || cat.description || cat.categoryName || "—";
        },
        formattedCreatedDate() {
            const d =
                this.ticketData &&
                (this.ticketData.createdAt || this.ticketData.createdDate);
            if (!d) return "—";
            const date = new Date(d);
            return isNaN(date.getTime())
                ? "—"
                : date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                  });
        },
        formattedCreatedDateTime() {
            const d =
                this.ticketData &&
                (this.ticketData.createdAt || this.ticketData.createdDate);
            if (!d) return "—";
            const date = new Date(d);
            return isNaN(date.getTime())
                ? "—"
                : date
                      .toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                      })
                      .toUpperCase();
        },
        displayScope() {
            const raw = this.ticketData;
            return raw && raw.scope ? String(raw.scope) : "";
        },
        displayTenant() {
            const raw = this.ticketData;
            if (!raw || !raw.tenant) return "";
            const t = raw.tenant;
            return typeof t === "string" ? t : t.name || t.tenantName || "";
        },
        slaData() {
            const raw = this.ticketData;
            return raw && (raw.sla || raw.slaProgress != null);
        },
        slaHealth() {
            const raw = this.ticketData;
            if (!raw) return 85;
            if (raw.slaProgress != null)
                return Math.min(100, Math.max(0, Number(raw.slaProgress)));
            if (raw.sla && raw.sla.health != null) return raw.sla.health;
            return 85;
        },
        slaStarted() {
            const d =
                this.ticketData &&
                (this.ticketData.createdAt || this.ticketData.startDate);
            if (!d) return "—";
            const diff = Date.now() - new Date(d).getTime();
            const days = Math.floor(diff / (24 * 60 * 60 * 1000));
            return days === 0 ? "TODAY" : days + "D";
        },
        slaRemaining() {
            const raw = this.ticketData;
            if (!raw || !raw.sla) return "";
            const h = raw.sla.remainingHours;
            if (h != null) return h + "H";
            return "";
        },
        attachmentCount() {
            const raw = this.ticketData;
            const arr = raw && raw.attachments;
            return Array.isArray(arr) ? arr.length : 0;
        },
        attachmentList() {
            const raw = this.ticketData;
            const arr = raw && raw.attachments;
            return Array.isArray(arr) ? arr : [];
        },
        activityTimeline() {
            const raw = this.ticketData;
            const history = raw && raw.changeHistory;
            if (!Array.isArray(history)) return [];
            return [...history].sort((a, b) => {
                const da = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const db = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return db - da;
            });
        },
        commentTimeline() {
            const raw = this.ticketData;
            const history = raw && raw.worknoteHistory;
            if (!Array.isArray(history)) return [];
            return [...history].sort((a, b) => {
                const da = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const db = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return db - da;
            });
        },
        commentCount() {
            const raw = this.ticketData;
            const arr = raw && raw.worknoteHistory;
            return Array.isArray(arr) ? arr.length : 0;
        },
        teamList() {
            return this.GetAdminList || [];
        },
        filteredTeamList() {
            const q = (this.memberSearchQuery || "").toLowerCase().trim();
            const list = this.teamList || [];
            if (!q) return list;
            return list.filter((m) => {
                const name = this.getMemberName(m).toLowerCase();
                const email = (
                    (m.authentication && m.authentication.email) ||
                    ""
                ).toLowerCase();
                return name.includes(q) || email.includes(q);
            });
        },
        filteredEscalateManagers() {
            return this.teamList || [];
        },
        canEscalate() {
            return (
                this.selectedAuthorityLevel != null &&
                this.escalationReason &&
                this.escalationReason.trim()
            );
        },
        assignCategory() {
            const raw = this.ticketData;
            const scope = raw && raw.scope;
            return scope ? String(scope).toUpperCase() : "CUSTOMER";
        },
        assignedToId() {
            const raw = this.ticketData;
            if (!raw) return null;
            const at = raw.assignTo;
            if (!at) return null;
            return (
                (typeof at === "object" && at.id) ||
                (typeof at === "object" && at._id) ||
                at
            );
        },
    },
    watch: {
        ticketId: {
            immediate: true,
            handler(id) {
                if (id && !this.GetTicketList.length) {
                    this.$store
                        .dispatch("ticketManagement/fetchTicketList")
                        .finally(() => {
                            this.loading = false;
                        });
                } else {
                    this.loading = false;
                }
            },
        },
    },
    mounted() {
        this.$store.dispatch("ticketManagement/fetchTeamList");
        this.$store.dispatch("ticketManagement/fetchCategoryList");
        if (this.GetTicketList.length) {
            this.loading = false;
        }
    },
    methods: {
        goBack() {
            this.$router.push({ name: "ticketManagement" });
        },
        syncTicket() {
            this.loading = true;
            this.$store
                .dispatch("ticketManagement/fetchTicketList")
                .finally(() => {
                    this.loading = false;
                    this.$bvToast.toast("Ticket data refreshed", {
                        title: "Success",
                        variant: "success",
                        solid: true,
                        autoHideDelay: 2000,
                    });
                });
        },
        getInitials(member) {
            const name = this.getMemberName(member);
            if (!name) return "?";
            return name
                .trim()
                .split(/\s+/)
                .map((p) => p[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
        },
        getManagerRole(manager) {
            if (!manager) return "";
            const role =
                manager.category ||
                manager.userType ||
                manager.role ||
                (manager.authentication && manager.authentication.userType) ||
                "";
            return typeof role === "string"
                ? role.toUpperCase().replace(/_/g, " ")
                : "";
        },
        getMemberName(member) {
            if (!member) return "";
            const n = member.name || {};
            const first = n.first || "";
            const last = n.last || "";
            const full = [first, last].filter(Boolean).join(" ");
            return (
                full ||
                (member.authentication && member.authentication.userName) ||
                ""
            );
        },
        getCommentImageUrls(entry) {
            if (!entry) return [];
            const att = entry.attachments;
            if (!att) return [];
            const urls = [];
            if (Array.isArray(att)) {
                att.forEach((a) => {
                    const u = a && (a.url || a.path || a.src);
                    if (u) urls.push(u);
                });
            } else if (att && typeof att === "object") {
                const u = att.url || att.path || att.src;
                if (u) urls.push(u);
            }
            return urls;
        },
        resetEscalateForm() {
            this.selectedAuthorityLevel = null;
            this.escalationReason = "";
        },
        buildAssignToPayload(manager) {
            const name = this.getMemberName(manager);
            const role = this.getManagerRole(manager);
            return {
                id: manager._id,
                name: name || "",
                email:
                    (manager.authentication && manager.authentication.email) ||
                    "",
                phone:
                    (manager.authentication && manager.authentication.phone) ||
                    manager.phone ||
                    "",
                userType: role || "EMPLOYEE",
            };
        },
        async escalateToManager(manager) {
            if (!manager || this.assignUpdating || !this.canEscalate) return;
            this.assignUpdating = true;
            try {
                await this.$store.dispatch(
                    "ticketManagement/escalateTicketAction",
                    {
                        id: this.ticketData._id,
                        assignCategory: this.assignCategory,
                        assignTo: this.buildAssignToPayload(manager),
                        escalationReason: this.escalationReason.trim(),
                        escalationLevel: this.selectedAuthorityLevel,
                    },
                );
                this.showEscalateModal = false;
                this.resetEscalateForm();
                this.$bvToast.toast("Ticket escalated successfully", {
                    title: "Success",
                    variant: "success",
                    solid: true,
                    autoHideDelay: 2000,
                });
            } catch (err) {
                this.$bvToast.toast(
                    (err && err.message) || "Failed to escalate ticket",
                    {
                        title: "Error",
                        variant: "danger",
                        solid: true,
                    },
                );
            } finally {
                this.assignUpdating = false;
            }
        },
        openImagePreview(url) {
            this.imagePreviewUrl = url;
            this.showImageModal = true;
        },
        onImageLoadError(e) {
            const img = e.target;
            if (img && img.nextElementSibling) {
                img.style.display = "none";
                img.nextElementSibling.style.display = "inline";
            }
        },
        getAttachmentUrl(att) {
            if (!att) return "";
            return att.url || att.path || att.src || "";
        },
        isImageAttachment(att) {
            if (!att) return false;
            const url = (att.url || att.path || att.src || "").toLowerCase();
            const name = (
                att.name ||
                att.fileName ||
                att.originalName ||
                ""
            ).toLowerCase();
            const pathPart = url.split("/").pop() || "";
            const ext = (
                pathPart.split(".").pop() ||
                name.split(".").pop() ||
                ""
            ).split("?")[0];
            const imgExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
            if (ext && imgExts.includes(ext)) return true;
            if (att.mimetype && att.mimetype.startsWith("image/")) return true;
            if (url.includes("/images/")) return true;
            return true;
        },
        formatTimelineDate(dateStr) {
            if (!dateStr) return "—";
            const d = new Date(dateStr);
            return isNaN(d.getTime())
                ? "—"
                : d
                      .toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                      })
                      .toUpperCase();
        },
        async selectStatus(status) {
            if (this.currentStatus === status || this.statusUpdating) return;
            this.statusUpdating = true;
            try {
                await this.$store.dispatch("ticketManagement/updateTicket", {
                    id: this.ticketData._id,
                    status,
                    rawTicket: this.ticketData,
                });
                this.$bvToast.toast("Status updated successfully", {
                    title: "Success",
                    variant: "success",
                    solid: true,
                    autoHideDelay: 2000,
                });
            } catch (err) {
                this.$bvToast.toast(err.message || "Failed to update status", {
                    title: "Error",
                    variant: "danger",
                    solid: true,
                });
            } finally {
                this.statusUpdating = false;
            }
        },
        async selectMember(member) {
            if (!member || this.assignUpdating) return;
            const id = member._id;
            if (this.assignedToId === id) return;
            this.assignUpdating = true;
            try {
                await this.$store.dispatch("ticketManagement/updateTicket", {
                    id: this.ticketData._id,
                    assignedTo: id,
                    rawTicket: this.ticketData,
                });
                this.$bvToast.toast("Assignee updated successfully", {
                    title: "Success",
                    variant: "success",
                    solid: true,
                    autoHideDelay: 2000,
                });
            } catch (err) {
                this.$bvToast.toast(err.message || "Failed to reassign", {
                    title: "Error",
                    variant: "danger",
                    solid: true,
                });
            } finally {
                this.assignUpdating = false;
            }
        },
    },
};
</script>

<style scoped>
.ticket-view-page {
    min-height: 100vh;
    background: var(--body-bg, #f7f8f9);
}

.ticket-view-content {
    margin-top: 16px;
}

.ticket-view-page >>> .section-card {
    border-radius: 0 !important;
}

.ticket-view-page >>> .card-wrap .card-header,
.ticket-view-page >>> .card-wrap .card-body,
.ticket-view-page >>> .card-wrap .card-footer {
    border-radius: 0 !important;
}

.header-left {
    flex: 1;
    min-width: 0;
}

.back-btn {
    background: none;
    border: none;
    padding: 8px;
    margin: -8px 8px -8px -8px;
    color: #5e6c84;
    cursor: pointer;
    border-radius: 4px;
}

.back-btn:hover {
    background: #ebecf0;
    color: #172b4d;
}

.header-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.ticket-id-display {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--slate-900, #172b4d);
}

.status-badge,
.severity-badge {
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
}

.badge-status-new {
    background: var(--warning-bg, #fff3cd);
    color: var(--warning, #856404);
}
.badge-status-in-progress {
    background: var(--info-bg, #cce5ff);
    color: var(--info, #004085);
}
.badge-status-resolved {
    background: var(--success-bg, #d4edda);
    color: var(--success, #155724);
}
.badge-status-closed {
    background: var(--slate-200, #e2e3e5);
    color: var(--slate-700, #383d41);
}

.badge-severity-low {
    background: var(--success-bg, #d4edda);
    color: var(--success, #155724);
}
.badge-severity-medium {
    background: var(--warning-bg, #fff3cd);
    color: var(--warning, #856404);
}
.badge-severity-high {
    background: var(--danger-bg, #f8d7da);
    color: var(--danger, #721c24);
}
.badge-severity-critical {
    background: var(--danger-bg, #f5c6cb);
    color: var(--danger, #721c24);
}

.ticket-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--slate-900, #172b4d);
    margin: 0;
    line-height: 1.3;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.action-btn {
    font-size: 0.75rem;
    font-weight: 600;
}

.escalate-btn {
    padding: 6px 12px;
}

.ticket-hero-card {
    margin-bottom: 16px;
}

.ticket-hero {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.hero-left {
    min-width: 0;
    flex: 1;
}

.card-header-custom {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--slate-600, #5e6c84);
}

.section-card >>> .card-header {
    background: var(--slate-50, #fafbfc);
    border-bottom: 1px solid var(--slate-200, #e9ecef);
    padding: 12px 16px;
}

.context-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px 24px;
}

.context-item {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
}

.context-label {
    color: var(--slate-600, #5e6c84);
    margin-right: 6px;
}

.context-value {
    color: var(--slate-900, #172b4d);
    font-weight: 500;
}

.description-text {
    font-size: 0.9rem;
    color: var(--slate-900, #172b4d);
    line-height: 1.6;
    white-space: pre-wrap;
}

.timeline-tabs >>> .nav-tabs {
    border-bottom: 1px solid var(--slate-200, #e9ecef);
}

.timeline-tabs >>> .nav-link {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--slate-600, #5e6c84);
}

.timeline-tabs >>> .nav-link.active,
.timeline-tabs >>> .nav-link:hover {
    color: var(--primary, #0052cc);
}

.timeline-item {
    display: flex;
    gap: 16px;
    padding: 12px 0;
}

.timeline-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #fff8e6;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.timeline-actor {
    font-size: 0.75rem;
    font-weight: 600;
    color: #5e6c84;
}

.timeline-text {
    font-size: 0.875rem;
    color: #172b4d;
}

.timeline-description {
    font-size: 0.8rem;
    color: #5e6c84;
    margin-top: 4px;
}

.timeline-date {
    font-size: 0.7rem;
    color: #97a0af;
}

.timeline-comment-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.timeline-comment-item .timeline-content {
    flex: 1;
    min-width: 0;
}

.comment-attachment-icons {
    flex-shrink: 0;
    display: flex;
    gap: 6px;
}

.comment-img-thumb-btn {
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid #dfe1e6;
    border-radius: 4px;
    background: #f4f5f7;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.comment-img-thumb-btn:hover {
    border-color: #0052cc;
    background: #e6f0ff;
}

.comment-img-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.comment-img-fallback {
    font-size: 1rem;
    color: #0052cc;
}

.attachments-list {
    padding: 8px 0;
}

.attachment-item {
    margin-bottom: 16px;
}

.attachment-image-wrap {
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #e9ecef;
    background: #f8f9fa;
}

.attachment-img {
    max-width: 100%;
    height: auto;
    display: block;
}

.attachment-link,
.attachment-link-fallback {
    color: #0052cc;
    text-decoration: none;
    font-size: 0.875rem;
}

.attachment-link:hover,
.attachment-link-fallback:hover {
    text-decoration: underline;
}

.sla-progress {
    padding: 0;
}

.sla-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.sla-health {
    color: #0052cc;
}

.sla-progress-bar {
    height: 8px;
    border-radius: 4px;
}

.sla-footer {
    display: flex;
    justify-content: space-between;
    font-size: 0.7rem;
    color: #97a0af;
    margin-top: 8px;
}

.sla-remaining {
    color: #de350b;
}

.status-options {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.status-option {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
}

.status-option:hover {
    background: #f4f5f7;
}

.status-option.active {
    background: #e6f0ff;
}

.status-radio {
    margin-right: 10px;
}

.status-label {
    flex: 1;
    font-size: 0.875rem;
}

.member-list {
    max-height: 240px;
    overflow-y: auto;
}

.member-item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border-radius: 6px;
    cursor: pointer;
    gap: 10px;
    transition: background 0.2s;
}

.member-item:hover {
    background: #f4f5f7;
}

.member-item.selected {
    background: #e6f0ff;
}

.member-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #0052cc;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    flex-shrink: 0;
}

.member-name {
    font-size: 0.875rem;
    flex: 1;
}

/* Escalate Ticket Modal */
.escalate-ticket-modal >>> .modal-content {
    border-radius: 0;
    overflow: hidden;
    border: none;
}

.escalate-modal-body {
    padding: 16px;
    max-height: 400px;
    overflow-y: auto;
}

.escalate-alert {
    background: var(--danger-bg, #f8d7da);
    color: var(--danger, #721c24);
    border: 1px solid rgba(220, 53, 69, 0.25);
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 0.855rem;
    margin-bottom: 12px;
}

.escalate-field {
    margin-bottom: 20px;
}

.escalate-field:last-child {
    margin-bottom: 0;
}

.escalate-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--slate-600, #5e6c84);
    display: block;
    margin-bottom: 8px;
}

.escalate-select {
    border: 2px solid var(--slate-200, #dfe1e6);
    border-radius: 6px;
}

.escalate-manager-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.escalate-manager-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border: 1px solid var(--slate-200, #e9ecef);
    border-radius: 6px;
    background: var(--white, #fff);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.escalate-manager-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--danger, #dc3545);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
    flex-shrink: 0;
}

.escalate-manager-info {
    flex: 1;
    min-width: 0;
}

.escalate-manager-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--slate-900, #172b4d);
}

.escalate-manager-role {
    font-size: 0.75rem;
    color: var(--slate-600, #5e6c84);
    margin-top: 2px;
}

.escalate-assign-btn {
    border-radius: 10px;
    font-weight: 600;
}

@media (max-width: 991px) {
    .ticket-hero {
        flex-direction: column;
    }

    .header-actions {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>
